import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import userAPI from "../../api/UserApi";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
// import "./LoginCanvas.css";

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const IconMail = () => (
  <svg className="lc-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2.5 5.5l7.5 5.5 7.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="1" y="4" width="18" height="12" rx="2" strokeLinejoin="round" />
  </svg>
);

const IconLock = () => (
  <svg className="lc-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="9" width="12" height="9" rx="2" />
    <path d="M7 9V6a3 3 0 0 1 6 0v3" strokeLinecap="round" />
    <circle cx="10" cy="13.5" r="1" fill="currentColor" />
  </svg>
);

const IconEye = ({ show }) =>
  show ? (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z" />
      <circle cx="10" cy="10" r="3" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3l14 14M8.5 8.6A3 3 0 0 0 10 13a3 3 0 0 0 3-3 3 3 0 0 0-.6-1.8" />
      <path d="M6.1 6.2C3.9 7.5 2 10 2 10s3 6 8 6a8.2 8.2 0 0 0 3.9-1M10 4c5 0 8 6 8 6s-.8 1.6-2.2 3" />
    </svg>
  );

// ─── Decorative Visual Panel Elements ────────────────────────────────────────

const GeometricGrid = () => (
  <svg className="lc-geo-grid" viewBox="0 0 600 700" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
    {/* Concentric rectangles */}
    <rect x="60"  y="60"  width="480" height="580" stroke="white" strokeWidth="0.4" opacity="0.15" />
    <rect x="100" y="100" width="400" height="500" stroke="white" strokeWidth="0.4" opacity="0.1"  />
    <rect x="140" y="140" width="320" height="420" stroke="white" strokeWidth="0.4" opacity="0.08" />

    {/* Diagonal cross lines */}
    <line x1="60"  y1="60"  x2="540" y2="640" stroke="white" strokeWidth="0.3" opacity="0.08" />
    <line x1="540" y1="60"  x2="60"  y2="640" stroke="white" strokeWidth="0.3" opacity="0.08" />

    {/* Vertical rhythm lines */}
    <line x1="200" y1="60"  x2="200" y2="640" stroke="white" strokeWidth="0.3" opacity="0.06" />
    <line x1="300" y1="60"  x2="300" y2="640" stroke="white" strokeWidth="0.3" opacity="0.06" />
    <line x1="400" y1="60"  x2="400" y2="640" stroke="white" strokeWidth="0.3" opacity="0.06" />

    {/* Corner accent marks */}
    <path d="M60 100 L60 60 L100 60"  stroke="white" strokeWidth="1.2" opacity="0.35" fill="none" />
    <path d="M500 60 L540 60 L540 100" stroke="white" strokeWidth="1.2" opacity="0.35" fill="none" />
    <path d="M60 600 L60 640 L100 640" stroke="white" strokeWidth="1.2" opacity="0.35" fill="none" />
    <path d="M500 640 L540 640 L540 600" stroke="white" strokeWidth="1.2" opacity="0.35" fill="none" />

    {/* Center diamond */}
    <polygon points="300,220 360,300 300,380 240,300" stroke="white" strokeWidth="0.5" opacity="0.12" fill="none" />
    <polygon points="300,250 340,300 300,350 260,300" stroke="white" strokeWidth="0.5" opacity="0.09" fill="none" />

    {/* Radial spokes from center */}
    {[0,45,90,135,180,225,270,315].map((deg, i) => {
      const r = (deg * Math.PI) / 180;
      return (
        <line
          key={i}
          x1={300 + 30 * Math.cos(r)}
          y1={300 + 30 * Math.sin(r)}
          x2={300 + 120 * Math.cos(r)}
          y2={300 + 120 * Math.sin(r)}
          stroke="white"
          strokeWidth="0.4"
          opacity="0.1"
        />
      );
    })}
    <circle cx="300" cy="300" r="28"  stroke="white" strokeWidth="0.6" opacity="0.12" fill="none" />
    <circle cx="300" cy="300" r="8"   stroke="white" strokeWidth="0.8" opacity="0.2"  fill="none" />

    {/* Horizontal rule near bottom */}
    <line x1="140" y1="520" x2="460" y2="520" stroke="white" strokeWidth="0.5" opacity="0.18" />
    <line x1="200" y1="530" x2="400" y2="530" stroke="white" strokeWidth="0.3" opacity="0.1"  />
  </svg>
);

// ─── Animation Variants ───────────────────────────────────────────────────────

const panelVariants = {
  hidden: { clipPath: "inset(0 100% 0 0)" },
  visible: {
    clipPath: "inset(0 0% 0 0)",
    transition: { duration: 1.0, ease: [0.76, 0, 0.24, 1] },
  },
};

const visualContentVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1, y: 0,
    transition: { delay: 0.42, duration: 0.9, ease: [0.33, 1, 0.68, 1] },
  },
};

const cardVariants = {
  hidden:   { opacity: 0, y: 32 },
  visible:  { opacity: 1, y: 0, transition: { delay: 0.18, duration: 0.8, ease: [0.33, 1, 0.68, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.52 } },
};

const fadeUp = {
  hidden:  { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.33, 1, 0.68, 1] } },
};

const fieldErrorVariants = {
  hidden:  { opacity: 0, y: -6, height: 0 },
  visible: { opacity: 1, y: 0,  height: "auto", transition: { duration: 0.04 } },
  exit:    { opacity: 0, y: -4, height: 0,      transition: { duration: 0.04 } },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = { email: "", password: "" };
  const [formData,     setFormData]     = useState(INITIAL_FORM_DATA);
  const [errors,       setErrors]       = useState({});
  const [loading,      setLoading]      = useState(false);
  const [rememberMe,   setRememberMe]   = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const emailRegex      = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail    = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};
    if (!formData.email)           newErrors.email    = "Please enter your email address";
    else if (!isValidEmail)        newErrors.email    = "Please enter a valid email";
    if (!formData.password)        newErrors.password = "Please enter your password";
    else if (!isValidPassword)     newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await userAPI.login(formData);
      if (res.code === 200) {
        localStorage.setItem("token", JSON.stringify({ email: res.data.email, username: res.data.username }));
        localStorage.setItem("accessToken", res.data.accessToken);
        setAccessToken(res.data.accessToken);
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lc-wrapper">

      {/* ── LEFT: Visual Panel ── */}
      <motion.div
        className="lc-visual"
        variants={panelVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="lc-visual-bg" />
        <GeometricGrid />
        <div className="lc-visual-overlay" />

        <motion.div
          className="lc-visual-content"
          variants={visualContentVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="lc-visual-eyebrow">Heritage · Craft · Legacy</div>

          <h2 className="lc-visual-headline">
            Timeless<br />
            <em>Elegance</em>
          </h2>

          <div className="lc-visual-rule">
            <span className="lc-visual-rule-dot" />
            <span className="lc-visual-rule-line" />
            <span className="lc-visual-rule-dot" />
          </div>

          <p className="lc-visual-sub">
            Curated ethnic fashion rooted in centuries of artisanal craft —
            where every thread tells a story.
          </p>

          <div className="lc-visual-brand">FATIMA CREATION</div>
        </motion.div>

        {/* Bottom stripe */}
        <div className="lc-visual-stripe" />
      </motion.div>

      {/* ── RIGHT: Form Panel ── */}
      <div className="lc-form-panel">

        {/* Top accent bar */}
        <div className="lc-form-topbar">
          <span className="lc-form-topbar-text">SIGN IN</span>
          <span className="lc-form-topbar-line" />
        </div>

        <motion.div
          className="lc-card"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >

          {/* Header */}
          <div className="lc-card-header">
            <h1 className="lc-title">Welcome<br />Back</h1>
            <p className="lc-subtitle">Access your curated wardrobe</p>
          </div>

          {/* Ornament divider */}
          <div className="lc-ornament">
            <span className="lc-ornament-line" />
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="lc-ornament-icon">
              <path d="M10 1 L11.5 8.5 L19 10 L11.5 11.5 L10 19 L8.5 11.5 L1 10 L8.5 8.5 Z" stroke="var(--color-accent-main)" strokeWidth="0.8" fill="none" />
              <circle cx="10" cy="10" r="2" fill="var(--color-accent-main)" opacity="0.4" />
            </svg>
            <span className="lc-ornament-line" />
          </div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            noValidate
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >

            {/* Email Field */}
            <motion.div className="lc-field" variants={fadeUp}>
              <label htmlFor="lc-email" className="lc-label">Email Address</label>
              <div className={`lc-input-wrap ${focusedField === "email" ? "is-focused" : ""} ${errors.email ? "is-error" : ""} ${formData.email && !errors.email ? "is-filled" : ""}`}>
                <IconMail />
                <input
                  id="lc-email"
                  type="email"
                  name="email"
                  className="lc-input"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  autoComplete="email"
                  required
                />
                {formData.email && !errors.email && (
                  <span className="lc-input-check">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="var(--color-accent-main)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </div>
                {errors.email && (
                  <motion.span
                    className="lc-error"
                    variants={fieldErrorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    key="email-err"
                  >
                    {errors.email}
                  </motion.span>
                )}
            </motion.div>

            {/* Password Field */}
            <motion.div className="lc-field" variants={fadeUp}>
              <label htmlFor="lc-password" className="lc-label">Password</label>
              <div className={`lc-input-wrap ${focusedField === "password" ? "is-focused" : ""} ${errors.password ? "is-error" : ""}`}>
                <IconLock />
                <input
                  id="lc-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="lc-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="lc-eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <IconEye show={showPassword} />
                </button>
              </div>
                {errors.password && (
                  <motion.span
                    className="lc-error"
                    variants={fieldErrorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    key="pass-err"
                  >
                    {errors.password}
                  </motion.span>
                )}
            </motion.div>

            {/* Options Row */}
            <motion.div className="lc-options" variants={fadeUp}>
              <label className="lc-remember">
                <input
                  type="checkbox"
                  className="lc-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="lc-checkbox-custom" />
                <span className="lc-remember-label">Remember me</span>
              </label>
              <Link to="/forgot-password" className="lc-forgot">Forgot password?</Link>
            </motion.div>

            {/* Submit */}
            <motion.div variants={fadeUp}>
              <motion.button
                type="submit"
                className="lc-btn-primary"
                disabled={loading}
                whileHover={!loading ? { y: -2 } : {}}
                whileTap={!loading ? { y: 0, scale: 0.99 } : {}}
                transition={{ duration: 0.18 }}
              >
                {loading ? (
                  <span className="lc-btn-loading">
                    <span className="lc-spinner" />
                    Signing In…
                  </span>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="lc-btn-arrow">
                      <path d="M1 7h12M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )}
              </motion.button>
            </motion.div>

          </motion.form>

          {/* Register Row */}
          <motion.div
            className="lc-register"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.4 }}
          >
            <span className="lc-register-text">Don't have an account?</span>
            <Link to="/users/register" className="lc-register-link">Create Account</Link>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
}